import React from "react";
import { formatDistanceToNow } from "date-fns";
import { MoreHorizontal, Pencil, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { RuledList, RuledListItem } from "@/components/shared/ruled-list";
import { ReviewWithUser } from "@/types/reviews.types";
import { StarRating } from "./star-rating";

interface ReviewsListProps {
  reviews: ReviewWithUser[];
  isLoading?: boolean;
  error?: string | null;
  currentUserId?: string;
  onEditReview?: (review: ReviewWithUser) => void;
  onDeleteReview?: (reviewId: string) => void;
  className?: string;
}

export const ReviewsList: React.FC<ReviewsListProps> = ({
  reviews, isLoading = false, error = null, currentUserId, onEditReview, onDeleteReview, className,
}) => {
  if (isLoading) return <p className="py-6 text-[13.5px] text-muted-foreground">Loading reviews…</p>;
  if (error) return <p className="py-6 text-[13.5px] text-brand">Could not load reviews: {error}</p>;
  if (reviews.length === 0) return <p className="py-6 text-[13.5px] text-muted-foreground">No reviews yet. Be the first.</p>;

  return (
    <RuledList className={className}>
      {reviews.map((review) => {
        const own = currentUserId ? review.userId === currentUserId : false;
        return (
          <RuledListItem key={review.id} className="items-start py-3">
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="font-semibold">{own ? "You" : review.user.name}</span>
                <span className="text-[12.5px] text-muted-foreground">
                  {formatDistanceToNow(new Date(review.createdAt), { addSuffix: true })}
                </span>
              </div>
              <StarRating rating={review.rating} size="sm" />
              {review.reviewText && <p className="max-w-[60ch] text-[13.5px] leading-relaxed">{review.reviewText}</p>}
            </div>
            {own && (onEditReview || onDeleteReview) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon-sm" aria-label="Review actions">
                    <MoreHorizontal aria-hidden />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {onEditReview && (
                    <DropdownMenuItem onClick={() => onEditReview(review)}>
                      <Pencil aria-hidden /> Edit review
                    </DropdownMenuItem>
                  )}
                  {onDeleteReview && (
                    <DropdownMenuItem onClick={() => onDeleteReview(review.id)} className="text-brand">
                      <Trash2 aria-hidden /> Delete review
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </RuledListItem>
        );
      })}
    </RuledList>
  );
};
